import { FC, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { getFeedbackType } from 'utils/models/Feedback';
import { getPrettyDate } from 'utils/util/util';
import ModalFeedbackEditorButton from 'views/Feedback/ModalFeedbackEditorButton';
import Loading from 'views/Widgets/Loading';
import { FeedbackApiResponse } from '../types';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  feedbackId: number;
  callback: (feedback) => void;
}

const EditModal: FC<Props> = ({ isOpen, feedbackId, toggle, callback }) => {
  const { formatMessage, locale } = useIntl();

  const { data, error, status } = useConfirmApi<FeedbackApiResponse>({
    url: `/admin/feedback/${feedbackId}`,
    method: 'GET',
    disabled: !isOpen,
    clearOnChange: true,
    clearOnError: true,
  });

  const object = useMemo(() => {
    if (!data) {
      return null;
    }
    return { ...data, type: getFeedbackType(data, formatMessage) };
  }, [data, formatMessage]);

  useEffect(() => {
    if (status === 'ERROR') {
      // @ts-expect-error
      if (error?.status === 404) {
        toast.warn(
          formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.edit_modal.error.feedback_not_owned',
            defaultMessage:
              'You do not have permission to modify this feedback. It may have been deleted or you may have lost the ownership, since belongs to people you may not have admin capability against anymore.',
          })
        );
      } else {
        toast.error(
          formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.edit_modal.error.fetching_feedback_data',
            defaultMessage: 'Error fetching feedback data',
          })
        );
      }
    }
  }, [error, formatMessage, status]);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.feedback_and_recognition_admin.edit_modal.message.fetching_feedback_data',
          defaultMessage: 'Fetching feedback data',
        })}
      />
    );
  }

  return (
    <>
      {!!object && (
        <ModalFeedbackEditorButton
          modalTitle={formatMessage(
            {
              id: 'app.views.administration.content_administration.feedback_and_recognition_admin.edit_modal.title',
              defaultMessage:
                'Edit Feedback from {personFullName} made on {date}',
            },
            {
              // @ts-expect-error
              personFullName: object.author_person.full_name,
              // @ts-expect-error
              date: getPrettyDate({ dateString: object.created_at, locale }),
            }
          )}
          adminFeedback={object}
          isOpen={isOpen}
          toggle={toggle}
          hideButton={true}
          keepOpenOnSuccess={true}
          toastOnCallbackEnabled={false}
          callback={callback}
        />
      )}
    </>
  );
};

export default EditModal;
